'use client'

import { useMemo, useState, type FormEvent } from 'react'
import Link from 'next/link'
import { TextField } from '@/app/components/atoms/forms/TextField'
import { MultiSelectField } from '@/app/components/atoms/forms/MultiSelectField'
import { Button } from '@/app/components/atoms/Button'
import { FieldMessage, useFieldIds } from '@/app/components/atoms/forms/field'
import { PROVINCES } from '@/app/lib/constants'
import { route } from '@/app/lib/routing'
import type { NewsletterSubscribeBody, NewsletterUpdateBody } from '@/app/lib/api/types-api'
import { isValidEmail, validateNameField } from './constants'
import { subscribeNewsletterAction, updateNewsletterAction } from './actions'
import styles from './NewsletterForm.module.scss'

// PROVINCES[0] = { slug: 'todos', label: 'España (todas)' } — sentinel de "todas las provincias"
const SPAIN_ALL = 'todos'
const PROVINCE_OPTIONS = PROVINCES.map((p) => ({ label: p.label, value: p.slug }))
const REAL_PROVINCE_SLUGS = PROVINCES.slice(1).map((p) => p.slug)

// Sentinel para "todas las categorías" (se filtra antes de enviar)
const CATEGORIES_ALL = '__all_categories__'

/** Valores para precargar el formulario en modo edición. */
export interface NewsletterFormInitialValues {
  name: string
  surname: string
  secondSurname: string
  email: string
  genres: string[]
  locations: string[]
}

export interface NewsletterFormProps {
  /** Categorías para el multiselect (del `menu` del layout). */
  categories?: { name: string }[]
  /** Id de suscripción (opaco, de la URL de edición). Su presencia activa el modo edición. */
  subscriptionId?: string
  /** Valores para precargar el formulario. Solo relevante junto a `subscriptionId`. */
  initialValues?: NewsletterFormInitialValues
}

/**
 * Formulario de alta/edición de newsletter. Cliente (estado + validación).
 * Sin `subscriptionId`: alta, envía vía Server Action `subscribeNewsletterAction` (POST).
 * Con `subscriptionId`: edición — email de solo lectura, envía vía `updateNewsletterAction`
 * (PATCH) incluyendo el `id`. No es un módulo CMS.
 */
export function NewsletterForm({ categories = [], subscriptionId, initialValues }: NewsletterFormProps) {
  const isEdit = Boolean(subscriptionId)
  const realCategoryValues = useMemo(() => categories.map((c) => c.name), [categories])
  const categoryOptions = useMemo(
    () => [
      { label: 'Todas', value: CATEGORIES_ALL },
      ...categories.map((c) => ({ label: c.name, value: c.name })),
    ],
    [categories],
  )
  const [nombre, setNombre] = useState(initialValues?.name ?? '')
  const [apellido1, setApellido1] = useState(initialValues?.surname ?? '')
  const [apellido2, setApellido2] = useState(initialValues?.secondSurname ?? '')
  const [categorias, setCategorias] = useState<string[]>(initialValues?.genres ?? [])
  const [provincias, setProvincias] = useState<string[]>(initialValues?.locations ?? [])
  const [email, setEmail] = useState(initialValues?.email ?? '')
  const [consent, setConsent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const [nombreError, setNombreError] = useState('')
  const [apellido1Error, setApellido1Error] = useState('')
  const [apellido2Error, setApellido2Error] = useState('')
  const [categoriaError, setCategoriaError] = useState('')
  const [provinciaError, setProvinciaError] = useState('')
  const [emailError, setEmailError] = useState('')
  const [consentError, setConsentError] = useState('')
  const [submitError, setSubmitError] = useState('')

  const { fieldId: consentId, messageId: consentMsgId } = useFieldIds()

  const validateNombre = (v: string) =>
    validateNameField(v, 'El nombre', 'Por favor introduce tu nombre.')
  const validateApellido1 = (v: string) =>
    validateNameField(v, 'El primer apellido', 'Por favor introduce el primer apellido.')
  const validateApellido2 = (v: string) => validateNameField(v, 'El segundo apellido')
  const validateEmail = (v: string) =>
    !v.trim() || !isValidEmail(v) ? 'Por favor verifique el email ingresado.' : ''

  const validateAll = () => {
    const nErr = validateNombre(nombre)
    const a1Err = validateApellido1(apellido1)
    const a2Err = validateApellido2(apellido2)
    const realCats = categorias.filter((v) => v !== CATEGORIES_ALL)
    const realProvs = provincias.filter((v) => v !== SPAIN_ALL)
    const cErr = realCats.length === 0 ? 'Por favor selecciona al menos una categoría.' : ''
    const pErr = realProvs.length === 0 ? 'Por favor selecciona al menos una provincia.' : ''
    const eErr = validateEmail(email)
    const chErr = consent ? '' : 'Debe aceptar las políticas de privacidad y condiciones.'
    setNombreError(nErr)
    setApellido1Error(a1Err)
    setApellido2Error(a2Err)
    setCategoriaError(cErr)
    setProvinciaError(pErr)
    setEmailError(eErr)
    setConsentError(chErr)
    return !nErr && !a1Err && !a2Err && !cErr && !pErr && !eErr && !chErr
  }

  const canSubmit = useMemo(
    () =>
      nombre.trim().length > 0 &&
      apellido1.trim().length > 0 &&
      categorias.filter((v) => v !== CATEGORIES_ALL).length > 0 &&
      provincias.filter((v) => v !== SPAIN_ALL).length > 0 &&
      isValidEmail(email) &&
      consent,
    [nombre, apellido1, categorias, provincias, email, consent],
  )

  const makeSentinelHandler = (
    sentinel: string,
    realValues: string[],
    getCurrent: () => string[],
    setState: (v: string[]) => void,
    clearError: () => void,
  ) =>
    (next: string[]) => {
      const current = getCurrent()
      const hadAll = current.includes(sentinel)
      const hasAll = next.includes(sentinel)
      if (hasAll && !hadAll) {
        setState([sentinel, ...realValues])
      } else if (!hasAll && hadAll) {
        setState([])
      } else {
        const real = next.filter((v) => v !== sentinel)
        setState(real.length === realValues.length ? [sentinel, ...real] : real)
      }
      clearError()
    }

  const handleCategoryChange = makeSentinelHandler(
    CATEGORIES_ALL,
    realCategoryValues,
    () => categorias,
    setCategorias,
    () => { if (categoriaError) setCategoriaError('') },
  )

  const handleProvinciaChange = makeSentinelHandler(
    SPAIN_ALL,
    REAL_PROVINCE_SLUGS,
    () => provincias,
    setProvincias,
    () => { if (provinciaError) setProvinciaError('') },
  )

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!validateAll()) return
    setSubmitError('')
    setLoading(true)
    try {
      const body: NewsletterSubscribeBody = {
        name: nombre.trim(),
        surname: apellido1.trim(),
        second_surname: apellido2.trim(),
        genres: { name: categorias.filter((v) => v !== CATEGORIES_ALL) },
        locations_name: { name: provincias.filter((v) => v !== SPAIN_ALL) },
        email: email.trim(),
        legal: consent ? '1' : '0',
      }

      const { ok } = isEdit
        ? await updateNewsletterAction({ ...body, id: subscriptionId as string } satisfies NewsletterUpdateBody)
        : await subscribeNewsletterAction(body)

      if (ok) setSubmitted(true)
      else if (isEdit) setSubmitError('No se han podido actualizar tus preferencias. Inténtalo de nuevo más tarde.')
      else setSubmitError('No se ha podido completar la suscripción. Inténtalo de nuevo más tarde.')
    } catch (error) {
      console.error('[NewsletterForm] submit failed', error)
      setSubmitError(
        isEdit
          ? 'No se han podido actualizar tus preferencias. Inténtalo de nuevo más tarde.'
          : 'No se ha podido completar la suscripción. Inténtalo de nuevo más tarde.',
      )
    } finally {
      setLoading(false)
    }
  }

  const formContent = submitted ? (
    <div className={styles.success} role="status" aria-live="polite">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="m9 12 2 2 4-4" />
      </svg>
      <p className={styles.successText}>
        {isEdit ? 'Tus preferencias han sido actualizadas.' : '¡Gracias! Te avisaremos de las mejores entradas.'}
      </p>
    </div>
  ) : (
    <form className={styles.formGroup} onSubmit={handleSubmit} noValidate>
      <div className={styles.nameRow}>
        <TextField
          label="Nombre"
          value={nombre}
          required
          errorText={nombreError || undefined}
          onChange={(v) => {
            setNombre(v)
            if (nombreError) setNombreError('')
          }}
          onBlur={() => setNombreError(validateNombre(nombre))}
        />
        <TextField
          label="Primer apellido"
          value={apellido1}
          required
          errorText={apellido1Error || undefined}
          onChange={(v) => {
            setApellido1(v)
            if (apellido1Error) setApellido1Error('')
          }}
          onBlur={() => setApellido1Error(validateApellido1(apellido1))}
        />
        <TextField
          label="Segundo apellido"
          value={apellido2}
          errorText={apellido2Error || undefined}
          onChange={(v) => {
            setApellido2(v)
            if (apellido2Error) setApellido2Error('')
          }}
          onBlur={() => setApellido2Error(validateApellido2(apellido2))}
        />
      </div>

      <div className={styles.twoColRow}>
        <MultiSelectField
          label="Categorías"
          required
          values={categorias}
          options={categoryOptions}
          sentinelValues={[CATEGORIES_ALL]}
          errorText={categoriaError || undefined}
          onChange={handleCategoryChange}
        />
        <MultiSelectField
          label="Provincias"
          required
          values={provincias}
          options={PROVINCE_OPTIONS}
          sentinelValues={[SPAIN_ALL]}
          errorText={provinciaError || undefined}
          onChange={handleProvinciaChange}
        />
      </div>

      <div className={styles.inputRow}>
        <div className={styles.emailField}>
          <TextField
            label="Email"
            type="email"
            value={email}
            required
            disabled={isEdit}
            errorText={emailError || undefined}
            onChange={(v) => {
              setEmail(v)
              if (emailError) setEmailError('')
            }}
            onBlur={() => setEmailError(validateEmail(email))}
          />
        </div>
        <Button type="submit" size="l" className={styles.submit} loading={loading} disabled={loading || !canSubmit}>
          {isEdit ? 'Guardar cambios' : 'Suscribirme'}
        </Button>
      </div>

      <div className={styles.consentRow}>
        <input
          id={consentId}
          type="checkbox"
          checked={consent}
          onChange={(e) => {
            setConsent(e.target.checked)
            if (consentError) setConsentError('')
          }}
          aria-describedby={consentError ? consentMsgId : undefined}
          aria-invalid={consentError ? true : undefined}
          className={styles.consentCheckbox}
        />
        <div className={styles.consentContent}>
          <label htmlFor={consentId} className={styles.consentLabel}>
            He leído y acepto la{' '}
            <Link href={route('/politica-privacidad-y-condiciones/')} className={styles.privacyLink}>
              política de privacidad y condiciones
            </Link>
            <span aria-hidden="true"> *</span>
          </label>
          <FieldMessage id={consentMsgId} error={consentError || undefined} />
        </div>
      </div>

      <p className={styles.supportText}>
        * Datos necesarios para poder ofrecerte una experiencia basada en tus propios
        intereses.
      </p>

      {submitError && (
        <p className={styles.submitError} role="alert">
          {submitError}
        </p>
      )}
    </form>
  )

  if (isEdit) {
    return <div className={styles.formCol}>{formContent}</div>
  }

  return (
    <section id="newsletter-section" className={styles.section} aria-labelledby="newsletter-heading">
      <div className={styles.wrap}>
        <div className={styles.grid}>
          <div>
            <p className={styles.eyebrow}>Newsletter</p>
            <h2 id="newsletter-heading" className={styles.title}>No te pierdas ningún plan</h2>
            <p className={styles.desc}>
              Recibe primero las mejores entradas, preventas exclusivas y novedades directamente en
              tu correo.
            </p>
          </div>

          <div className={styles.formCol}>{formContent}</div>
        </div>
      </div>
    </section>
  )
}
